import { buttonVariants } from "@patient-watch/ui/button";
import {
    DEFAULT_SELECT_FIELD_SIZE,
    normalizeSelectFieldSize,
    SELECT_FIELD_LABEL_CLASSNAME,
    selectFieldComboboxTriggerClassName,
    type SelectFieldSize,
} from "@patient-watch/ui/select";

import { Command, CommandGroup, CommandItem, CommandSeparator } from "@patient-watch/ui/command";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@patient-watch/ui/dialog";
import { FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@patient-watch/ui/form";
import { Popover, PopoverContent, PopoverTrigger } from "@patient-watch/ui/popover";
import { DialogTrigger } from "@radix-ui/react-dialog";
import { Check, ChevronsUpDown, Lock, Plus, Search, X } from "lucide-react";
import React from "react";
import { useFormContext } from "react-hook-form";
import { cn, FieldIconButton } from "@patient-watch/ui";
import type { OptionT } from "./Select";
import { ComboBoxTriggerValue } from "./components/ComboBoxTriggerValue";
import { FormFieldLockedControl } from "./components/formFieldLockedControl";
import { FormFieldLabelWithAccessory } from "./components/formFieldLabelRow";
import { RichLabel } from "./components/RichLabel";
import { getOptionTestId, getTestId } from "./utils/getTestId";
import { groupOptions } from "./utils/groupOptions";
import type { LooseRHFField } from "./utils/loose-rhf-field";
import { reactNodeToPlainString } from "./utils/reactNodeToPlainString";

interface Props {
    label?: string;
    labelClassName?: string;
    name: string;
    description?: React.ReactNode;
    options: OptionT[];
    disabled?: boolean;
    autoFocus?: boolean;
    clearable?: boolean;
    addOption?: React.ReactNode | ((close: () => void) => React.ReactNode);
    /** Sticky row at the bottom of the list, e.g. a link to manage team contacts. */
    footer?: React.ReactNode;
    placeholder?: string;
    /** When set and non-empty, matching options (by `value` as user id) are listed first, then alphabetically within each group. */
    favouriteUserIds?: ReadonlySet<string>;
    /** Renders at the start of each option row (e.g. favourite toggle). Clicks should not select the option. */
    optionTrailing?: (option: OptionT) => React.ReactNode;
    /** App locale tag, e.g. "de-DE". Defaults to English copy. */
    locale?: string;
    /** Shown at the end of the label row (defaults to FormFieldLabelAccessoryProvider when set). */
    labelTrailing?: React.ReactNode;
    /** Matches `@patient-watch/forms` Select sizing (`sm` | `md`, default `md`). */
    size?: SelectFieldSize;
    /** Replaces the locale default for the search box placeholder. */
    searchPlaceholder?: string;
    /** Replaces the locale default for the no-results message. */
    emptyText?: string;
}

/** ponytail: inline copy - packages/forms has no i18n dependency, callers pass their locale. */
function comboBoxCopy(locale?: string) {
    if (locale === "de-DE") {
        return {
            search: "Suchen…",
            empty: "Keine Optionen gefunden.",
            addOption: "Option hinzufügen",
            clear: "Löschen",
            select: "Auswählen",
        };
    }
    if (locale === "fr-FR") {
        return {
            search: "Rechercher…",
            empty: "Aucune option trouvée.",
            addOption: "Ajouter une option",
            clear: "Effacer",
            select: "Sélectionner",
        };
    }
    if (locale === "it-IT") {
        return {
            search: "Cerca…",
            empty: "Nessuna opzione trovata.",
            addOption: "Aggiungi un'opzione",
            clear: "Cancella",
            select: "Seleziona",
        };
    }
    if (locale === "es-ES") {
        return {
            search: "Buscar…",
            empty: "No se han encontrado opciones.",
            addOption: "Añadir una opción",
            clear: "Borrar",
            select: "Seleccionar",
        };
    }
    if (locale === "da-DK") {
        return {
            search: "Søg…",
            empty: "Ingen valgmuligheder fundet.",
            addOption: "Tilføj valgmulighed",
            clear: "Ryd",
            select: "Vælg",
        };
    }
    return {
        search: "Search...",
        empty: "No options found.",
        addOption: "Add an Option",
        clear: "Clear",
        select: "Select",
    };
}

function ComboBox({
    options,
    placeholder,
    disabled,
    label,
    labelClassName,
    name,
    description,
    clearable,
    addOption,
    footer,
    favouriteUserIds,
    optionTrailing,
    locale,
    labelTrailing,
    size = DEFAULT_SELECT_FIELD_SIZE,
    searchPlaceholder,
    emptyText,
}: Props) {
    const form = useFormContext();
    const fieldSize = normalizeSelectFieldSize(size);
    const copy = comboBoxCopy(locale);
    const listId = React.useId();
    const inputRef = React.useRef<HTMLInputElement>(null);
    const [open, setOpen] = React.useState(false);
    const [addOptionOpen, setAddOptionOpen] = React.useState(false);
    const [search, setSearch] = React.useState("");
    const [activeValue, setActiveValue] = React.useState<string | null>(null);

    const displayedOptions = React.useMemo(() => {
        const query = search.toLowerCase();
        const filtered = (options ?? []).filter((o) => {
            if (!query) return true;
            const label = reactNodeToPlainString(o.label).toLowerCase();
            const value = String(o.value ?? "").toLowerCase();
            const searchText = (o.searchText ?? "").toLowerCase();
            return label.includes(query) || value.includes(query) || searchText.includes(query);
        });
        if (!favouriteUserIds || favouriteUserIds.size === 0) {
            return filtered;
        }
        const sortByLabel = (a: OptionT, b: OptionT) =>
            reactNodeToPlainString(a.label).localeCompare(reactNodeToPlainString(b.label), undefined, {
                sensitivity: "base",
            });
        const fav = filtered.filter((o) => favouriteUserIds.has(String(o.value)));
        const rest = filtered.filter((o) => !favouriteUserIds.has(String(o.value)));
        return [...[...fav].sort(sortByLabel), ...[...rest].sort(sortByLabel)];
    }, [options, search, favouriteUserIds]);

    const sections = React.useMemo(() => groupOptions(displayedOptions), [displayedOptions]);
    const selectable = React.useMemo(
        () =>
            displayedOptions.filter(
                (option) => !option.locked && option.value != null && String(option.value).length > 0
            ),
        [displayedOptions]
    );
    const firstSelectableValue = selectable[0] ? String(selectable[0].value) : null;

    React.useEffect(() => {
        setActiveValue(firstSelectableValue);
    }, [firstSelectableValue]);

    React.useEffect(() => {
        if (!open) return;
        inputRef.current?.focus();
    }, [open]);

    const scrollActiveRef = React.useRef(false);

    React.useEffect(() => {
        if (!open || !activeValue || !scrollActiveRef.current) return;
        scrollActiveRef.current = false;
        document.getElementById(`${listId}-${activeValue}`)?.scrollIntoView({ block: "nearest" });
    }, [open, activeValue, listId]);

    function moveActive(delta: number) {
        if (selectable.length === 0) return;
        const index = selectable.findIndex((option) => String(option.value) === activeValue);
        const nextIndex = Math.min(selectable.length - 1, Math.max(0, (index < 0 ? 0 : index) + delta));
        const next = selectable[nextIndex];
        if (!next) return;
        scrollActiveRef.current = true;
        setActiveValue(String(next.value));
    }

    return (
        <FormField
            control={form.control}
            name={name}
            render={({ field }: { field: LooseRHFField }) => {
                const selectedOption = (options ?? []).find((o) => o.value === field.value);
                return (
                    <FormItem className="w-full">
                        {label && (
                            <FormFieldLabelWithAccessory
                                label={
                                    <FormLabel className={cn(SELECT_FIELD_LABEL_CLASSNAME, labelClassName)}>
                                        <RichLabel>{label}</RichLabel>
                                    </FormLabel>
                                }
                                labelTrailing={labelTrailing}
                            />
                        )}
                        {description && <FormDescription>{description}</FormDescription>}
                        <FormFieldLockedControl>
                            <Popover
                                open={open}
                                onOpenChange={(next) => {
                                    setOpen(next);
                                    if (!next) setSearch("");
                                }}
                            >
                                <div className="relative flex-1 min-w-0">
                                    <PopoverTrigger asChild>
                                        <FormControl>
                                            <button
                                                type="button"
                                                role="combobox"
                                                aria-controls={listId}
                                                aria-expanded={open}
                                                data-testid={getTestId(name, "trigger")}
                                                disabled={disabled}
                                                className={cn(
                                                    selectFieldComboboxTriggerClassName(fieldSize),
                                                    !field.value && "text-slate-700",
                                                    clearable &&
                                                        !disabled &&
                                                        field.value != null &&
                                                        field.value !== "" &&
                                                        "[&>span]:pr-6"
                                                )}
                                            >
                                                <ComboBoxTriggerValue
                                                    selected={field.value ? selectedOption : undefined}
                                                    placeholder={placeholder ?? copy.select}
                                                />
                                                {disabled ? (
                                                    <Lock className="opacity-75 size-4 shrink-0" aria-hidden />
                                                ) : (
                                                    <ChevronsUpDown
                                                        className="opacity-75 size-4 shrink-0"
                                                        aria-hidden
                                                    />
                                                )}
                                            </button>
                                        </FormControl>
                                    </PopoverTrigger>
                                    {clearable && !disabled && field.value != null && field.value !== "" && (
                                        <FieldIconButton
                                            tabIndex={-1}
                                            aria-label={copy.clear}
                                            className="top-1/2 right-8 absolute -translate-y-1/2"
                                            onPointerDown={(event) => event.preventDefault()}
                                            onClick={() => field.onChange(null)}
                                        >
                                            <X aria-hidden />
                                        </FieldIconButton>
                                    )}
                                </div>
                                <PopoverContent
                                    id={listId}
                                    align="start"
                                    className="relative p-0 rounded-lg w-[var(--radix-popover-trigger-width)] min-w-[var(--radix-popover-trigger-width)] overflow-hidden"
                                    onOpenAutoFocus={(event) => {
                                        event.preventDefault();
                                        inputRef.current?.focus();
                                    }}
                                >
                                    <Command shouldFilter={false}>
                                        <div className="flex items-center gap-2 px-2 py-1 border-border border-b">
                                            <Search className="opacity-50 size-4 shrink-0" aria-hidden />
                                            <input
                                                ref={inputRef}
                                                data-testid="combobox-search-input"
                                                value={search}
                                                placeholder={searchPlaceholder ?? copy.search}
                                                aria-autocomplete="list"
                                                aria-controls={listId}
                                                aria-activedescendant={
                                                    activeValue ? `${listId}-${activeValue}` : undefined
                                                }
                                                className="flex-1 bg-transparent px-0 py-1 border-0 outline-none focus:outline-none ring-0 focus:ring-0 min-w-0 dark:placeholder:text-slate-400 placeholder:text-slate-700 text-sm"
                                                onChange={(event) => setSearch(event.target.value)}
                                                onKeyDown={(event) => {
                                                    if (event.key === "ArrowDown") {
                                                        event.preventDefault();
                                                        moveActive(1);
                                                    } else if (event.key === "ArrowUp") {
                                                        event.preventDefault();
                                                        moveActive(-1);
                                                    } else if (event.key === "Enter") {
                                                        event.preventDefault();
                                                        const selected = selectable.find(
                                                            (option) => String(option.value) === activeValue
                                                        );
                                                        if (!selected) return;
                                                        field.onChange(selected.value);
                                                        setSearch("");
                                                        setOpen(false);
                                                    }
                                                }}
                                            />
                                        </div>
                                        {selectable.length === 0 ? (
                                            <div className="py-6 text-sm text-center">{emptyText ?? copy.empty}</div>
                                        ) : null}
                                        <div
                                            className={cn(
                                                "max-h-[400px] overflow-y-auto",
                                                (addOption || footer) && "pb-12"
                                            )}
                                        >
                                            {sections.map((section, sectionIndex) => (
                                                <React.Fragment key={`${section.group ?? "ungrouped"}-${sectionIndex}`}>
                                                    {sectionIndex > 0 && <CommandSeparator />}
                                                    <CommandGroup heading={section.group}>
                                                        {section.options.map((o) => (
                                                            <CommandItem
                                                                id={`${listId}-${String(o.value)}`}
                                                                data-testid={getOptionTestId(o)}
                                                                role="option"
                                                                aria-selected={o.value === field.value}
                                                                data-selected={
                                                                    String(o.value) === activeValue ? "" : undefined
                                                                }
                                                                value={String(o.value)}
                                                                key={String(o.value)}
                                                                showCheck={false}
                                                                disabled={
                                                                    o.locked ||
                                                                    o.value == null ||
                                                                    String(o.value).length === 0
                                                                }
                                                                className={cn(
                                                                    optionTrailing &&
                                                                        "flex flex-row items-center gap-1",
                                                                    String(o.value) === activeValue &&
                                                                        "bg-indigo-100 text-slate-900 dark:bg-indigo-800 dark:text-slate-50"
                                                                )}
                                                                onMouseMove={() => {
                                                                    if (String(o.value) !== activeValue) {
                                                                        setActiveValue(String(o.value));
                                                                    }
                                                                }}
                                                                onMouseDown={(e) => {
                                                                    // Keep focus in the field so choosing an item is not lost to blur.
                                                                    e.preventDefault();
                                                                }}
                                                                onSelect={(selectedValue) => {
                                                                    const selected = options?.find(
                                                                        (opt) => String(opt.value) === selectedValue
                                                                    );
                                                                    if (!selected || selected.locked) return;
                                                                    field.onChange(selected.value);
                                                                    setSearch("");
                                                                    setOpen(false);
                                                                }}
                                                            >
                                                                {optionTrailing ? (
                                                                    <span
                                                                        role="presentation"
                                                                        className="shrink-0"
                                                                        onMouseDown={(e) => {
                                                                            e.preventDefault();
                                                                            e.stopPropagation();
                                                                        }}
                                                                        onPointerDown={(e) => e.stopPropagation()}
                                                                    >
                                                                        {optionTrailing(o)}
                                                                    </span>
                                                                ) : null}
                                                                <span className="flex flex-col flex-1 min-w-0">
                                                                    <span className="inline-flex items-center gap-2 min-w-0">
                                                                        {o.icon ? (
                                                                            <span className="shrink-0">{o.icon}</span>
                                                                        ) : null}
                                                                        <span className="truncate">{o.label}</span>
                                                                    </span>
                                                                    {o.description ? (
                                                                        <span className="text-slate-500 dark:text-slate-400 text-xs truncate">
                                                                            {o.description}
                                                                        </span>
                                                                    ) : null}
                                                                </span>
                                                                <Check
                                                                    size={16}
                                                                    className={cn(
                                                                        "ml-auto shrink-0",
                                                                        o.value === field.value
                                                                            ? "opacity-100"
                                                                            : "opacity-0"
                                                                    )}
                                                                />
                                                            </CommandItem>
                                                        ))}
                                                    </CommandGroup>
                                                </React.Fragment>
                                            ))}
                                        </div>
                                    </Command>
                                    {(addOption || footer) && (
                                        <div className="right-0 bottom-0 left-0 absolute bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-800 border-t">
                                            {footer}
                                            {addOption ? (
                                                <Dialog open={addOptionOpen} onOpenChange={setAddOptionOpen}>
                                                    <DialogTrigger
                                                        className={cn(
                                                            buttonVariants({
                                                                size: "sm",
                                                                variant: "ghost",
                                                            }),
                                                            "flex items-center gap-2 w-full"
                                                        )}
                                                    >
                                                        <Plus size={20} />
                                                        {copy.addOption}
                                                    </DialogTrigger>
                                                    <DialogContent onSubmit={(event) => event.stopPropagation()}>
                                                        <DialogHeader>
                                                            <DialogTitle>{copy.addOption}</DialogTitle>
                                                        </DialogHeader>
                                                        {typeof addOption === "function"
                                                            ? addOption(() => setAddOptionOpen(false))
                                                            : addOption}
                                                    </DialogContent>
                                                </Dialog>
                                            ) : null}
                                        </div>
                                    )}
                                </PopoverContent>
                            </Popover>
                        </FormFieldLockedControl>

                        <FormMessage />
                    </FormItem>
                );
            }}
        />
    );
}

export default ComboBox;
